import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { motion } from 'framer-motion'
import { MapPinIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { api } from '../services/api'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ErrorMessage from '../components/common/ErrorMessage'

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.05 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
}

export default function CitiesPage() {
  usePageTitle('Garage Sales by City')
  const [citiesByState, setCitiesByState] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoading(true)
        const response = await api.locations.getCities()
        setCitiesByState(response.cities_by_state)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchCities()
  }, [])

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-center items-center py-8">
          <LoadingSpinner size="medium" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <ErrorMessage
        message={error}
        onRetry={() => window.location.reload()}
        className="max-w-2xl mx-auto mt-8"
      />
    )
  }

  // Sort states alphabetically
  const sortedStates = Object.keys(citiesByState).sort()

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
    >
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-text-muted mb-6">
        <Link to="/" className="link-subtle hover:text-text">Home</Link>
        <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="text-text font-medium">Cities</span>
      </nav>

      {/* Page Header */}
      <div className="flex items-start gap-3.5 mb-8">
        <div className="rounded-xl bg-primary-50 p-2.5 flex-shrink-0
          ring-4 ring-primary-50/50">
          <MapPinIcon className="h-6 w-6 text-primary-600" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text leading-tight">Garage Sales by City</h1>
          <p className="text-text-muted mt-1 text-sm sm:text-base">
            Browse garage sale events organized by location
          </p>
        </div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {sortedStates.map(state => (
          <motion.div
            key={state}
            variants={itemVariants}
            whileHover={{ y: -2, transition: { duration: 0.2 } }}
            className="card p-6 hover:shadow-md hover:border-border-strong transition-all duration-200"
          >
            <h2 className="text-xl font-semibold text-text mb-4">{state}</h2>
            <ul className="space-y-2">
              {citiesByState[state].map(entry => (
                <li key={entry.city}>
                  <Link
                    to={`/cities/${encodeURIComponent(`${entry.city}, ${state}`)}`}
                    className="text-primary-600 hover:text-primary-700 hover:underline
                      text-sm transition-colors min-h-[44px] flex items-center"
                  >
                    {entry.city}, {state}
                    <span className="text-text-muted ml-2 text-xs">({entry.sales_count})</span>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  )
}
